// "Latest AI news": a compact, filterable list of stories from /api/news.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import type { NewsCategory, NewsItem, NewsPayload } from "../../hooks/useAINews";
import { logActivity } from "../../api/activityLog";
import { faviconUrl as favicon, timeAgo } from "./format";

const LAST_SEEN_KEY = "ainav.news.lastSeen";
/** Stories in view at once; the rest scroll inside the card. */
const IN_VIEW = 5;

export const NEWS_TOPICS: { key: NewsCategory; label: string; tone: string }[] = [
  { key: "Real Estate & Construction", label: "Real estate", tone: "realestate" },
  { key: "Microsoft & Copilot", label: "Microsoft & Copilot", tone: "microsoft" },
  { key: "Models", label: "Models", tone: "models" },
  { key: "Tools & Apps", label: "Tools & apps", tone: "tools" },
  { key: "Business", label: "Business", tone: "business" },
  { key: "Policy & Safety", label: "Policy & safety", tone: "policy" },
  { key: "Hardware", label: "Hardware", tone: "hardware" },
];
const TOPIC = new Map(NEWS_TOPICS.map((t) => [t.key, t]));

/** When this person last looked, so newer stories get a "New" badge. Read
 *  once, then moved forward after they've had a look. */
function useLastSeen(ready: boolean): number {
  const [previous] = useState(() => {
    try {
      return Number(localStorage.getItem(LAST_SEEN_KEY)) || 0;
    } catch {
      return 0;
    }
  });
  useEffect(() => {
    if (!ready) return;
    const t = window.setTimeout(() => {
      try {
        localStorage.setItem(LAST_SEEN_KEY, String(Date.now()));
      } catch {
        // storage blocked: badges just won't persist
      }
    }, 4000);
    return () => window.clearTimeout(t);
  }, [ready]);
  return previous;
}

function Thumb({ item }: { item: NewsItem }) {
  const [failed, setFailed] = useState(false);
  if (item.image && !failed) {
    return (
      <span className="db-news-thumb">
        <img src={item.image} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setFailed(true)} />
      </span>
    );
  }
  return (
    <span className={`db-news-thumb db-news-thumb--icon db-tone--${TOPIC.get(item.category)?.tone ?? "tools"}`}>
      <img src={favicon(item.domain)} alt="" loading="lazy" referrerPolicy="no-referrer" />
    </span>
  );
}

function Row({ item, isNew }: { item: NewsItem; isNew: boolean }) {
  const topic = TOPIC.get(item.category);
  return (
    <li>
      <a
        className="db-news-row"
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => void logActivity("Feature", "AI News opened", `${item.source}: ${item.title}`, "news", item.id)}
      >
        <Thumb item={item} />
        <span className="db-news-body">
          <span className="db-news-title">{item.title}</span>
          <span className="db-news-summary">{item.summary}</span>
          <span className="db-news-meta">
            <img className="db-favicon" src={favicon(item.domain)} alt="" loading="lazy" referrerPolicy="no-referrer" />
            <span className="db-news-source">{item.source}</span>
            <span aria-hidden>·</span>
            <time dateTime={item.publishedAt}>{timeAgo(item.publishedAt)}</time>
            {topic ? <span className={`db-topic db-tone--${topic.tone}`}>{topic.label}</span> : null}
            {isNew ? <span className="db-new">New</span> : null}
          </span>
        </span>
        <ArrowUpRight className="db-news-go" size={16} aria-hidden />
      </a>
    </li>
  );
}

type Props = {
  data: NewsPayload | null;
  loading: boolean;
  error: string | null;
  filter: NewsCategory | "All";
  onFilter: (f: NewsCategory | "All") => void;
};

export function NewsFeed({ data, loading, error, filter, onFilter }: Props) {
  const lastSeen = useLastSeen(Boolean(data));
  const items = data?.items ?? [];
  const topics = useMemo(() => NEWS_TOPICS.filter((t) => items.some((i) => i.category === t.key)), [items]);
  const shown = filter === "All" ? items : items.filter((i) => i.category === filter);
  const visible = shown;

  // The box is as tall as the first five stories (they vary in height); the
  // rest scroll inside it.
  const listRef = useRef<HTMLUListElement>(null);
  const [boxHeight, setBoxHeight] = useState<number | undefined>(undefined);
  const [below, setBelow] = useState(0);
  const measure = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    const rows = list.children;
    if (rows.length <= IN_VIEW) return setBoxHeight(undefined);
    const last = rows[IN_VIEW - 1] as HTMLElement;
    setBoxHeight(last.offsetTop + last.offsetHeight);
  }, []);
  const track = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    // Stories still below the fold, for the "scroll for more" hint.
    const bottom = list.scrollTop + list.clientHeight;
    setBelow([...list.children].filter((c) => (c as HTMLElement).offsetTop + 8 > bottom).length);
  }, []);
  useLayoutEffect(() => {
    measure();
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [shown.length, filter, measure]);
  useEffect(() => {
    track();
  }, [boxHeight, shown.length, track]);
  useEffect(() => {
    const onResize = () => {
      measure();
      track();
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [measure, track]);
  const isNew = (i: NewsItem) => lastSeen > 0 && Date.parse(i.publishedAt) > lastSeen;
  const newCount = items.filter(isNew).length;

  return (
    <section className="db-card db-news" id="db-news" aria-labelledby="db-news-title">
      <div className="db-news-head">
        <div>
          <h2 id="db-news-title" className="db-section-title">
            Latest AI news
          </h2>
          <p className="db-section-sub">
            {data
              ? `${data.sources.length} sources · updated ${timeAgo(data.updatedAt)}${newCount ? ` · ${newCount} new since your last visit` : ""}`
              : "From AI companies, tech press and real estate media."}
          </p>
        </div>
      </div>

      {topics.length > 1 ? (
        <div className="db-chips" role="group" aria-label="Filter news by topic">
          {[{ key: "All" as const, label: "All" }, ...topics].map((t) => (
            <button
              key={t.key}
              type="button"
              className={`db-chip${filter === t.key ? " is-active" : ""}`}
              aria-pressed={filter === t.key}
              onClick={() => onFilter(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      ) : null}

      {error && !data ? (
        <p className="db-empty-text">{error}</p>
      ) : loading && !data ? (
        <div className="db-skel-rows" aria-busy="true" aria-label="Loading news">
          {Array.from({ length: 5 }, (_, i) => (
            <span key={i} />
          ))}
        </div>
      ) : !visible.length ? (
        <p className="db-empty-text">No stories on this topic right now.</p>
      ) : (
        <div className="db-news-box">
          <div className={`db-news-viewport${below ? " has-more" : ""}`}>
          <ul
            ref={listRef}
            className="db-news-list db-news-scroll"
            style={{ maxHeight: boxHeight }}
            onScroll={track}
            tabIndex={boxHeight ? 0 : undefined}
            aria-label={boxHeight ? `${shown.length} stories, scrollable` : undefined}
          >
            {visible.map((i) => (
              <Row key={i.id} item={i} isNew={isNew(i)} />
            ))}
          </ul>
          </div>
          {below ? (
            <button
              type="button"
              className="db-news-more"
              onClick={() => listRef.current?.scrollBy({ top: (boxHeight ?? 400) * 0.8, behavior: "smooth" })}
            >
              Scroll for {below} more <ChevronDown size={14} aria-hidden />
            </button>
          ) : null}
        </div>
      )}
    </section>
  );
}
